import { modulesPerColAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/modulesPerCol'
import { modulesPerRowAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/modulesPerRow'
import { rackingTypeAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/rackingType'
import type { ModuleLayout, PanelConfiguration, StudioSystemType } from 'types/studio/items'
import { parseIntegrationJson } from 'util/misc'
import {
  setGroundClearanceForModuleGrids,
  setModuleLayoutForModuleGrids,
  setModuleSpacingForModuleGrids,
  setRelativeTiltForModuleGrids,
} from '../nativeMounting/utils'
import {
  getVanDerValkVariantPickerFieldOrder,
  isSameSuitabilityRow,
  resolveRowForPanelLayoutApply,
  resolveVanDerValkSuitabilityRowForBom,
  VANDERVALK_PITCHED_SUITABILITY_GROUPING_VERSION,
} from './suitabilityOptions'
import { groupSuitabilityRowsByBaseLabel, resolveSuitabilityGroupKeyForSelection } from './vanDerValkSuitabilityLabels'
import {
  isVanDerValkSuitabilityConfigRow,
  type VanDerValkSuitabilityConfig,
  type VanDerValkSuitabilityModuleSpecs,
} from './types'
import {
  DEFAULT_SOLAR_RAMP_FIXATION,
  DEFAULT_SOLAR_RAMP_FOUNDATION_TYPE,
  DEFAULT_SOLAR_RAMP_MIDDLE_ZONE_DEFLECTOR,
  isSolarRampFoundationTypeAllowed,
  isVanDerValkSolarRampSystem,
} from './solarRampProfiles'

const SOLAR_RAMP_INTEGRATION_KEYS = [
  'foundationType',
  'flatRoofFixation',
  'anchorType',
  'fastenerType',
  'roofMaterial',
  'roofConstructionHeight',
] as const

function clearVanDerValkSolarRampIntegrationFields(vanDerValk: Record<string, unknown>): void {
  for (const key of SOLAR_RAMP_INTEGRATION_KEYS) {
    delete vanDerValk[key]
  }
}

function applyVanDerValkSolarRampIntegrationFields(
  vanDerValk: Record<string, unknown>,
  prevVdv: Record<string, unknown>,
  selected: VanDerValkSuitabilityConfig | null
): void {
  if (selected == null || !isVanDerValkSolarRampSystem(selected.system)) {
    clearVanDerValkSolarRampIntegrationFields(vanDerValk)
    return
  }

  const prevFoundation = prevVdv.foundationType
  vanDerValk.foundationType =
    typeof prevFoundation === 'string' && isSolarRampFoundationTypeAllowed(selected.system, prevFoundation)
      ? prevFoundation
      : DEFAULT_SOLAR_RAMP_FOUNDATION_TYPE

  const prevFixation = prevVdv.flatRoofFixation
  vanDerValk.flatRoofFixation =
    prevFixation === 'BALLASTING' || prevFixation === 'ANCHORING' ? prevFixation : DEFAULT_SOLAR_RAMP_FIXATION

  if (vanDerValk.flatRoofFixation !== 'ANCHORING') {
    delete vanDerValk.anchorType
    delete vanDerValk.fastenerType
    delete vanDerValk.roofMaterial
    delete vanDerValk.roofConstructionHeight
  }
}

/** Parse vendor tilt (degrees) for relative panel tilt. Returns null if missing or not a finite number. */
export function parseVanDerValkTiltDegrees(value: unknown): number | null {
  if (value === undefined || value === null) {
    return null
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/°\s*$/u, '').trim()
    if (trimmed === '') {
      return null
    }
    const n = parseFloat(trimmed)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function rawTiltFromVanDerValkRow(row: VanDerValkSuitabilityConfig): unknown {
  const r = row as unknown as Record<string, unknown>
  if (r.tilt !== undefined && r.tilt !== null) {
    return r.tilt
  }
  const pascal = r.Tilt
  if (pascal !== undefined && pascal !== null) {
    return pascal
  }
  return undefined
}

export function applyVanDerValkRelativeTiltFromRow(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig | null
): void {
  if (!system || row == null) {
    return
  }
  const deg = parseVanDerValkTiltDegrees(rawTiltFromVanDerValkRow(row))
  if (deg == null) {
    return
  }
  setRelativeTiltForModuleGrids(system, deg)
}

/**
 * Vendor lengths in millimetres (flat-roof suitability API: `pvModuleLift`, `gap`, etc.).
 * Studio distances are metres.
 */
export function parseVanDerValkMillimetresToMetres(value: unknown): number | null {
  if (value === undefined || value === null) {
    return null
  }
  let n: number | null = null
  if (typeof value === 'number') {
    n = Number.isFinite(value) ? value : null
  } else if (typeof value === 'string') {
    const trimmed = value
      .trim()
      .replace(/\s*mm\s*$/iu, '')
      .replace(/°\s*$/u, '')
      .trim()
    if (trimmed === '') {
      return null
    }
    const parsed = parseFloat(trimmed)
    n = Number.isFinite(parsed) ? parsed : null
  } else {
    return null
  }
  if (n == null) {
    return null
  }
  return n / 1000
}

function rawPvModuleLiftFromVanDerValkRow(row: VanDerValkSuitabilityConfig): unknown {
  const r = row as unknown as Record<string, unknown>
  if (r.pvModuleLift !== undefined && r.pvModuleLift !== null) {
    return r.pvModuleLift
  }
  const pascal = r.PvModuleLift
  if (pascal !== undefined && pascal !== null) {
    return pascal
  }
  return undefined
}

export function applyVanDerValkGroundClearanceFromRow(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig | null
): void {
  if (!system || row == null) {
    return
  }
  const metres = parseVanDerValkMillimetresToMetres(rawPvModuleLiftFromVanDerValkRow(row))
  if (metres == null) {
    return
  }
  setGroundClearanceForModuleGrids(system, metres)
}

function rawGapFromVanDerValkRow(row: VanDerValkSuitabilityConfig): unknown {
  const r = row as unknown as Record<string, unknown>
  if (r.gap !== undefined && r.gap !== null) {
    return r.gap
  }
  const pascal = r.Gap
  if (pascal !== undefined && pascal !== null) {
    return pascal
  }
  return undefined
}

/**
 * `gap` in mm → `moduleSpacing` [horizontal, vertical] in metres (same value both axes).
 */
export function applyVanDerValkModuleSpacingFromRow(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig | null
): void {
  if (!system || row == null) {
    return
  }
  const m = parseVanDerValkMillimetresToMetres(rawGapFromVanDerValkRow(row))
  if (m == null) {
    return
  }
  setModuleSpacingForModuleGrids(system, [m, m])
}

/** Map vendor `moduleOrientation` (e.g. LANDSCAPE) to studio `ModuleLayout`. */
export function parseVanDerValkModuleLayout(raw: unknown): ModuleLayout | null {
  if (raw === undefined || raw === null) {
    return null
  }
  const s0 = String(raw).trim().toLowerCase()
  if (!s0) {
    return null
  }
  const compact = s0.replace(/[_\s-]+/g, '')
  if (compact === 'l' || compact === 'landscape' || s0.includes('landscape')) {
    return 'landscape'
  }
  if (compact === 'p' || compact === 'portrait' || s0.includes('portrait')) {
    return 'portrait'
  }
  return null
}

function rawModuleOrientationFromVanDerValkRow(row: VanDerValkSuitabilityConfig): string {
  const r = row as unknown as Record<string, unknown>
  const mo = r.moduleOrientation
  const pascal = r.ModuleOrientation
  if (mo !== undefined && mo !== null) {
    return String(mo)
  }
  if (pascal !== undefined && pascal !== null) {
    return String(pascal)
  }
  return ''
}

/** Vendor ``fields[0].frame.middleZoneDeflector`` values we persist or send. */
export type VanDerValkMiddleZoneDeflectorVendor = 'ONLY_BACKPLATES' | 'OPEN_SYSTEM' | 'CLOSED_SYSTEM'

/**
 * Coerce ``middleZoneDeflector`` for a suitability row (NS portrait → CLOSED_SYSTEM; NS landscape → ONLY/CLOSED;
 * EW portrait → CLOSED_SYSTEM; EW landscape → OPEN/CLOSED).
 */
export function coerceVanDerValkMiddleZoneDeflectorForRow(
  prev: unknown,
  row: VanDerValkSuitabilityConfig
): VanDerValkMiddleZoneDeflectorVendor {
  if (isVanDerValkSolarRampSystem(row.system)) {
    if (prev === 'OPEN_SYSTEM' || prev === 'CLOSED_SYSTEM') {
      return prev
    }
    return DEFAULT_SOLAR_RAMP_MIDDLE_ZONE_DEFLECTOR
  }
  const place = String(row.placeSystem ?? '')
    .trim()
    .toUpperCase()
  const layout = parseVanDerValkModuleLayout(rawModuleOrientationFromVanDerValkRow(row))
  if (place === 'NS') {
    if (layout === 'portrait') {
      return 'CLOSED_SYSTEM'
    }
    if (layout === 'landscape') {
      if (prev === 'ONLY_BACKPLATES' || prev === 'CLOSED_SYSTEM') {
        return prev
      }
      return 'ONLY_BACKPLATES'
    }
    return 'ONLY_BACKPLATES'
  }
  if (place === 'EW') {
    if (layout === 'portrait') {
      return 'CLOSED_SYSTEM'
    }
    if (layout === 'landscape') {
      if (prev === 'OPEN_SYSTEM' || prev === 'CLOSED_SYSTEM') {
        return prev
      }
      return 'OPEN_SYSTEM'
    }
  }
  return 'ONLY_BACKPLATES'
}

export function applyVanDerValkOrientationFromRow(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig | null
): void {
  if (!system || row == null) {
    return
  }
  const layout = parseVanDerValkModuleLayout(rawModuleOrientationFromVanDerValkRow(row))
  if (layout == null) {
    return
  }
  setModuleLayoutForModuleGrids(system, layout)
}

/** East–west placement uses dual-tilt racking; all other placeSystem values use single-tilt. */
export function resolveVanDerValkRackingTypeFromRow(
  row: VanDerValkSuitabilityConfig | null
): Extract<PanelConfiguration, 'DUAL_TILT_RACK' | 'TILT_RACK'> | null {
  if (row == null) {
    return null
  }
  const place = String(row.placeSystem ?? '')
    .trim()
    .toUpperCase()
  return place === 'EW' ? 'DUAL_TILT_RACK' : 'TILT_RACK'
}

export function applyVanDerValkRackingFromRow(system: StudioSystemType, row: VanDerValkSuitabilityConfig | null): void {
  if (!system || row == null) {
    return
  }
  const rackingType = resolveVanDerValkRackingTypeFromRow(row)
  if (rackingType == null) {
    return
  }
  system.moduleGrids().forEach((moduleGrid) => {
    rackingTypeAccessorConfig.set({ moduleGrid, value: rackingType })
  })
}

/** Parse vendor dilatation limits (positive integers). */
export function parseVanDerValkPositiveInteger(value: unknown): number | null {
  if (value === undefined || value === null) {
    return null
  }
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10)
  if (!Number.isFinite(n) || n < 1) {
    return null
  }
  return Math.floor(n)
}

function rawDilatationMaxColumnsFromVanDerValkRow(row: VanDerValkSuitabilityConfig): unknown {
  const r = row as unknown as Record<string, unknown>
  if (r.dilatationMaxColumns !== undefined && r.dilatationMaxColumns !== null) {
    return r.dilatationMaxColumns
  }
  return r.DilatationMaxColumns
}

function rawDilatationMaxRowsFromVanDerValkRow(row: VanDerValkSuitabilityConfig): unknown {
  const r = row as unknown as Record<string, unknown>
  if (r.dilatationMaxRows !== undefined && r.dilatationMaxRows !== null) {
    return r.dilatationMaxRows
  }
  return r.DilatationMaxRows
}

export function getVanDerValkDilatationModuleLimitsFromRow(row: VanDerValkSuitabilityConfig | null): {
  modulesPerCol: number | null
  modulesPerRow: number | null
} {
  if (row == null) {
    return { modulesPerCol: null, modulesPerRow: null }
  }
  return {
    modulesPerCol: parseVanDerValkPositiveInteger(rawDilatationMaxColumnsFromVanDerValkRow(row)),
    modulesPerRow: parseVanDerValkPositiveInteger(rawDilatationMaxRowsFromVanDerValkRow(row)),
  }
}

/**
 * Single-tilt only: `dilatationMaxColumns` → modules per column, `dilatationMaxRows` → modules per row.
 * Run after `applyVanDerValkRackingFromRow` so vendor row counts override the racking setter’s default modulesPerRow.
 */
export function applyVanDerValkSingleTiltDilatationFromRow(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig | null
): void {
  if (!system || row == null) {
    return
  }
  if (resolveVanDerValkRackingTypeFromRow(row) !== 'TILT_RACK') {
    return
  }
  const { modulesPerCol, modulesPerRow } = getVanDerValkDilatationModuleLimitsFromRow(row)
  system.moduleGrids().forEach((moduleGrid) => {
    if (modulesPerCol != null) {
      modulesPerColAccessorConfig.set({ moduleGrid, value: modulesPerCol })
    }
    if (modulesPerRow != null) {
      modulesPerRowAccessorConfig.set({ moduleGrid, value: modulesPerRow })
    }
  })
}

/**
 * Pitched-roof (on-roof) dilatation: `dilatationMaxColumns` → modules per column, `dilatationMaxRows` →
 * modules per row, on all grids. Unlike `applyVanDerValkSingleTiltDilatationFromRow` there is no
 * `TILT_RACK` gate — pitched mounts flush, so the limits apply regardless of racking.
 */
export function applyVanDerValkDilatationModuleLimitsFromRow(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig | null
): void {
  if (!system || row == null) {
    return
  }
  const { modulesPerCol, modulesPerRow } = getVanDerValkDilatationModuleLimitsFromRow(row)
  system.moduleGrids().forEach((moduleGrid) => {
    if (modulesPerCol != null) {
      modulesPerColAccessorConfig.set({ moduleGrid, value: modulesPerCol })
    }
    if (modulesPerRow != null) {
      modulesPerRowAccessorConfig.set({ moduleGrid, value: modulesPerRow })
    }
  })
}

/** Apply vendor row values to module grids (tilt, spacing, racking, dilatation, etc.). */
export function applyVanDerValkPanelLayoutFromRow(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig | null,
  opts?: { respectExplicitOrientation?: boolean }
): void {
  if (!system || row == null) {
    return
  }
  const layoutRow =
    opts?.respectExplicitOrientation === true ? row : resolveRowForPanelLayoutApply(system, row)
  // Pitched-roof (on-roof): flush-mounted — only orientation + dilatation limits apply.
  // No tilt/ground-clearance/spacing/racking (rows carry no tilt/gap and empty placeSystem must not force TILT_RACK).
  if (system.mounting_type === 'onRoof') {
    applyVanDerValkOrientationFromRow(system, layoutRow)
    applyVanDerValkDilatationModuleLimitsFromRow(system, layoutRow)
    return
  }
  applyVanDerValkRelativeTiltFromRow(system, layoutRow)
  applyVanDerValkGroundClearanceFromRow(system, layoutRow)
  applyVanDerValkOrientationFromRow(system, layoutRow)
  applyVanDerValkModuleSpacingFromRow(system, layoutRow)
  applyVanDerValkRackingFromRow(system, layoutRow)
  applyVanDerValkSingleTiltDilatationFromRow(system, layoutRow)
}

type ApplyArgs = {
  suitabilityResult: VanDerValkSuitabilityConfig[]
  /** If true, clear selected; otherwise clear only when the previous pick is not in the new list */
  clearSelected?: boolean
  /** When set, persists alongside results (omit to keep previous `suitabilityModuleSpecs`). */
  suitabilityModuleSpecs?: VanDerValkSuitabilityModuleSpecs | null
  /**
   * Pitched-roof only: the `claddingType` (derived from roof type) the results were fetched for.
   * Persisted so the picker can invalidate cached results when the roof type (cladding) changes.
   */
  suitabilityCladdingType?: string | null
  /** Pitched STANDING_SEAM only: ``seamType`` sent with suitability. */
  suitabilitySeamType?: string | null
  /** Pitched only: cache key for ``claddingOptions`` sent with suitability. */
  suitabilityCladdingOptionsKey?: string | null
}

/**
 * Merges suitability results into `integration_json.vanDerValk` and issues SetValueCommand.
 * Dispatches `objectChanged` for `integration_json` so panels re-read, and for `mounting` when
 * this system uses Van der Valk so mounting calc (vendor BOM) runs like other mounting inputs.
 */
export function applyVanDerValkSuitabilityResultToSystem(system: StudioSystemType, update: ApplyArgs): void {
  if (!system) return

  const raw = system.integration_json
  const base = (parseIntegrationJson(raw) as Record<string, unknown> | null) || {}
  const prevVdv = (base.vanDerValk as Record<string, unknown> | undefined) || {}

  const nextList = update.suitabilityResult
  let selected: VanDerValkSuitabilityConfig | null

  if (nextList.length === 0) {
    selected = null
  } else if (update.clearSelected) {
    selected = null
  } else {
    selected = (prevVdv.selectedSuitability as VanDerValkSuitabilityConfig | null) ?? null
    if (selected) {
      const exactMatch = nextList.some((r) => isSameSuitabilityRow(r, selected as VanDerValkSuitabilityConfig))
      if (!exactMatch) {
        const fieldOrder = getVanDerValkVariantPickerFieldOrder(system.mounting_type)
        const resolved = resolveVanDerValkSuitabilityRowForBom(selected, nextList, fieldOrder)
        const { groups, order } = groupSuitabilityRowsByBaseLabel(nextList)
        const baseKey = resolveSuitabilityGroupKeyForSelection(resolved, groups, order)
        selected = groups.has(baseKey) ? resolved : null
      }
    }
  }

  const vanDerValk: Record<string, unknown> = {
    ...prevVdv,
    suitabilityResult: nextList,
    selectedSuitability: selected,
    ...(update.suitabilityModuleSpecs !== undefined ? { suitabilityModuleSpecs: update.suitabilityModuleSpecs } : {}),
    ...(update.suitabilityCladdingType !== undefined
      ? { suitabilityCladdingType: update.suitabilityCladdingType }
      : {}),
    ...(update.suitabilitySeamType !== undefined ? { suitabilitySeamType: update.suitabilitySeamType } : {}),
    ...(update.suitabilityCladdingOptionsKey !== undefined
      ? { suitabilityCladdingOptionsKey: update.suitabilityCladdingOptionsKey }
      : {}),
    suitabilityGroupingVersion: VANDERVALK_PITCHED_SUITABILITY_GROUPING_VERSION,
  }
  delete vanDerValk.middleZoneDeflector
  if (selected != null && isVanDerValkSuitabilityConfigRow(selected)) {
    selected = resolveRowForPanelLayoutApply(system, selected)
    vanDerValk.selectedSuitability = selected
    vanDerValk.middleZoneDeflector = coerceVanDerValkMiddleZoneDeflectorForRow(prevVdv.middleZoneDeflector, selected)
    applyVanDerValkSolarRampIntegrationFields(vanDerValk, prevVdv, selected)
  } else {
    clearVanDerValkSolarRampIntegrationFields(vanDerValk)
  }

  const integration_json = { ...base, vanDerValk } as unknown as typeof system.integration_json
  const commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
  window.editor.execute(new window.SetValueCommand(system, 'integration_json', integration_json, commandUUID))
  applyVanDerValkRackingFromSuitabilityRow(system, selected)
  window.editor.signals.objectChanged.dispatch(system, 'integration_json')
  if (selected) {
    applyVanDerValkPanelLayoutFromRow(system, selected)
  }
  if (system.mounting === 'vanDerValk') {
    window.editor.signals.objectChanged.dispatch(system, 'mounting')
  }
}

/**
 * Remove suitability cache from `integration_json.vanDerValk` (results, selection, module snapshot).
 * Preserves any other keys on `vanDerValk`; uses `{}` when nothing remains.
 */
export function clearVanDerValkSuitabilityIntegration(system: StudioSystemType): void {
  if (!system) return

  const raw = system.integration_json
  const base = (parseIntegrationJson(raw) as Record<string, unknown> | null) || {}
  const prevVdv = (base.vanDerValk as Record<string, unknown> | undefined) || {}
  const nextVdv: Record<string, unknown> = { ...prevVdv }
  delete nextVdv.suitabilityResult
  delete nextVdv.selectedSuitability
  delete nextVdv.suitabilityModuleSpecs
  delete nextVdv.suitabilityCladdingType
  delete nextVdv.suitabilitySeamType
  delete nextVdv.suitabilityCladdingOptionsKey
  delete nextVdv.suitabilityGroupingVersion
  delete nextVdv.bom
  delete nextVdv.middleZoneDeflector
  clearVanDerValkSolarRampIntegrationFields(nextVdv)
  const vanDerValk = Object.keys(nextVdv).length > 0 ? nextVdv : {}

  const integration_json = { ...base, vanDerValk } as unknown as typeof system.integration_json
  const commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
  window.editor.execute(new window.SetValueCommand(system, 'integration_json', integration_json, commandUUID))
  window.editor.signals.objectChanged.dispatch(system, 'integration_json')
  if (system.mounting === 'vanDerValk') {
    window.editor.signals.objectChanged.dispatch(system, 'mounting')
  }
}

/** Normalized vendor ``placeSystem`` (``NS`` or ``EW``), or empty when unknown. */
export function getVanDerValkPlaceSystem(row: VanDerValkSuitabilityConfig | null | undefined): string {
  if (row == null) {
    return ''
  }
  return String(row.placeSystem ?? '')
    .trim()
    .toUpperCase()
}

export function isVanDerValkEwPlaceSystem(row: VanDerValkSuitabilityConfig | null | undefined): boolean {
  return getVanDerValkPlaceSystem(row) === 'EW'
}

export function isVanDerValkNsPlaceSystem(row: VanDerValkSuitabilityConfig | null | undefined): boolean {
  return getVanDerValkPlaceSystem(row) === 'NS'
}

/**
 * Apply module racking from suitability: EW → dual-tilt, NS → single-tilt (all module grids).
 */
export function applyVanDerValkRackingFromSuitabilityRow(
  system: StudioSystemType,
  row: VanDerValkSuitabilityConfig | null
): void {
  if (!system || row == null || !isVanDerValkSuitabilityConfigRow(row)) {
    return
  }
  const place = getVanDerValkPlaceSystem(row)
  let racking: 'DUAL_TILT_RACK' | 'TILT_RACK' | null = null
  if (place === 'EW') {
    racking = 'DUAL_TILT_RACK'
  } else if (place === 'NS') {
    racking = 'TILT_RACK'
  }
  if (racking == null) {
    return
  }
  system.moduleGrids().forEach((moduleGrid) => {
    rackingTypeAccessorConfig.set({ moduleGrid, value: racking })
  })
}

export function applySelectedVanDerValkSuitability(
  system: StudioSystemType,
  selected: VanDerValkSuitabilityConfig | null,
  opts?: { respectExplicitOrientation?: boolean }
): void {
  if (!system) return

  const rowToPersist =
    selected != null && opts?.respectExplicitOrientation !== true
      ? resolveRowForPanelLayoutApply(system, selected)
      : selected

  const raw = system.integration_json
  const base = (parseIntegrationJson(raw) as Record<string, unknown> | null) || {}
  const prevVdv = (base.vanDerValk as Record<string, unknown> | undefined) || {}

  const vanDerValk: Record<string, unknown> = { ...prevVdv, selectedSuitability: rowToPersist }
  delete vanDerValk.middleZoneDeflector
  if (rowToPersist != null && isVanDerValkSuitabilityConfigRow(rowToPersist)) {
    vanDerValk.middleZoneDeflector = coerceVanDerValkMiddleZoneDeflectorForRow(prevVdv.middleZoneDeflector, rowToPersist)
    applyVanDerValkSolarRampIntegrationFields(vanDerValk, prevVdv, rowToPersist)
  } else {
    clearVanDerValkSolarRampIntegrationFields(vanDerValk)
  }
  const integration_json = { ...base, vanDerValk } as unknown as typeof system.integration_json
  const commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
  window.editor.execute(new window.SetValueCommand(system, 'integration_json', integration_json, commandUUID))
  applyVanDerValkRackingFromSuitabilityRow(system, rowToPersist)
  window.editor.signals.objectChanged.dispatch(system, 'integration_json')
  applyVanDerValkPanelLayoutFromRow(system, rowToPersist, opts)
  if (system.mounting === 'vanDerValk') {
    window.editor.signals.objectChanged.dispatch(system, 'mounting')
  }
}
